import { Button } from "primereact/button";
import { Chips } from "primereact/chips";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useState } from "react";

import { useCreateCategorizedDataset, useGetDefaultDatasetCategory } from "api/api-hooks/datasets-hooks";

import DatasetCategoryDropdown from "components/basic/dropdowns/dataset-category/DatasetCategoryDropdown";
import DatasetUnitSelector from "components/basic/dropdowns/dataset-unit-selector/DatasetUnitSelector";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";

import { BaseDatasetDTO } from "api/model/dataset/BaseDatasetDTO";
import CopyTextWithButton from "components/basic/copy-text-with-button/CopyTextWithButton";
import { FormattedDataForExportProps } from "components/basic/datasets-properties/DatasetsPropertiesTable";
import {
  DATASET_ACCESS_TYPES,
  DATASET_DATA_TYPES,
  DATASET_DATA_TYPES_FOR_CONTINUOUS,
  normalizeDataTypeForAccessType,
} from "components/dashboard/dataset-window/utils/utils";
import { T } from "services/lang/LanguageService";
import { DatasetAccessType, DatasetTypeForContinuous } from "utils/enums";

export const DATASET_DEFAULT_DIALOG_STATE = {
  unitSelectorValue: "",
  datasetTitle: "",
  dataType: DatasetTypeForContinuous.ARBITRARY,
  accessType: DatasetAccessType.NORMAL,
  defaultCategory: undefined,
};

interface ICreateMultipleDatasetsProps {
  closeDialog: () => void;
  setFormattedDataForExport: (value: Array<FormattedDataForExportProps>) => void;
}

interface ICommonDatasetProps {
  id: string;
  title: string;
  categoryId: number | undefined;
  dataType: string;
  accessType: string;
  unit: string;
  [key: string]: string | number | undefined;
}

const defaultDataset = {
  id: "",
  title: "",
  categoryId: undefined,
  dataType: DatasetTypeForContinuous.ARBITRARY,
  accessType: DatasetAccessType.NORMAL,
  unit: "",
};

const CreateMultipleDatasets = ({ closeDialog, setFormattedDataForExport }: ICreateMultipleDatasetsProps) => {
  const [defaultCategory, setDefaultCategory] = useState<number | undefined>(undefined);
  const [datasetsToCreate, setDatasetsToCreate] = useState<ICommonDatasetProps[]>([]);
  const [createdDatasets, setCreatedDatasets] = useState<Partial<BaseDatasetDTO>[]>([]);

  const [advancedDialogVisibility, setAdvancedDialogVisibility] = useState(false);
  const [prefixes, setPrefixes] = useState<string[]>([]);
  const [suffixes, setSuffixes] = useState<string[]>([]);
  const [datasetsCopy, setDatasetsCopy] = useState<ICommonDatasetProps[]>([]);
  const [advancedUsed, setAdvancedUsed] = useState<boolean>(false);
  const [lastDataset, setLastDataset] = useState<ICommonDatasetProps>(defaultDataset);

  const defaultCategoryQuery = useGetDefaultDatasetCategory();

  useEffect(() => {
    if (createdDatasets && createdDatasets.length > 0) {
      setFormattedDataForExport(
        createdDatasets
          .filter((dataset): dataset is BaseDatasetDTO => dataset !== undefined)
          .map((dataset: BaseDatasetDTO) => ({
            dataset: dataset.identifier ?? "",
            name: dataset.title ?? "",
            group: dataset.groupTitle ?? "",
            dataType: dataset.dataType ?? "",
            unit: dataset.unit ?? "",
            category: dataset.categoryTitle ?? "",
            datasetClass: dataset.datasetClass ?? "",
            accessType: dataset.accessType ?? "",
            decimalPlaces: dataset.decimalPlaces ?? 0,
            tags: dataset.tags ?? [],
          }))
      );
    }
  }, [createdDatasets]);

  useEffect(() => {
    if (defaultCategoryQuery.isFetched) {
      setDefaultCategory(defaultCategoryQuery.data?.id);
    }
    if (defaultCategoryQuery.isError) {
      easasAlert({ summary: T("error.error-fetching-category") });
    }
  }, [defaultCategoryQuery.data]);

  const generateUniqueId = (): string => {
    const timestamp = new Date().getTime().toString(16);
    const randomPart = Math.floor(Math.random() * 1000).toString(16);
    return `${timestamp}${randomPart}`;
  };

  const header = (
    <div className="flex gap-2 justify-content-between">
      <div className="flex gap-2">
        <DatasetCategoryDropdown value={defaultCategory} onChange={e => setDefaultCategory(e)} />
        <Button
          label={T("side-menu.advanced")}
          className="p-button-secondary"
          onClick={() => setAdvancedDialogVisibility(true)}
        />
        <Dialog
          onHide={() => setAdvancedDialogVisibility(false)}
          visible={advancedDialogVisibility}
          header={T("word.advanced-menu")}
          style={{ maxWidth: "600px", width: "300px" }}
        >
          <div className="flex flex-column gap-2">
            <p className="mb-0">{T("message.add-prefixes-press-enter-to-add")}</p>
            <Chips
              className="p-fluid"
              placeholder={T("word.prefix")}
              value={prefixes}
              onChange={e => setPrefixes(e.value ?? [])}
            />
            <p className="mb-0">{T("message.add-suffixes-press-enter-to-add")}</p>
            <Chips
              className="p-fluid"
              placeholder={T("word.suffix")}
              value={suffixes}
              onChange={e => setSuffixes(e.value ?? [])}
            />
            <Button
              label={T("action.generate")}
              icon="pi pi-check"
              className="component-editor-button icon-green"
              onClick={() => generateDatasetList()}
            />
          </div>
        </Dialog>
      </div>
      <div className="buttons flex gap-2">
        <Button
          icon="pi pi-plus"
          label={T("word.add")}
          className="component-editor-button icon-green"
          onClick={() => addDataset()}
        />
        {advancedUsed && (
          <Button
            label={T("action.reset")}
            className="p-button-secondary"
            onClick={() => {
              setDatasetsToCreate(datasetsCopy);
              setAdvancedUsed(false);
            }}
          />
        )}
      </div>
    </div>
  );

  const addDataset = () => {
    setDatasetsToCreate([
      ...datasetsToCreate,
      {
        id: generateUniqueId(),
        title: lastDataset.title,
        categoryId: defaultCategory,
        dataType: lastDataset.dataType,
        accessType: lastDataset.accessType,
        unit: lastDataset.unit,
      },
    ]);
  };

  const generateDatasetList = () => {
    const updatedDatasets = advancedUsed ? datasetsCopy : datasetsToCreate;
    const generatedDatasetList: ICommonDatasetProps[] = [];
    if (prefixes.length === 0 && suffixes.length === 0) {
      easasAlert({
        summary: T("error.validation-error"),
        detail: T("alert.nothing-to-add"),
        severity: "warn",
      });
      return;
    }

    if (updatedDatasets.length === 0) {
      easasAlert({
        summary: T("error.validation-error"),
        detail: T("alert.no-datasets-to-create"),
        severity: "warn",
      });
      return;
    }

    if (!advancedUsed) {
      setDatasetsCopy(updatedDatasets);
    }

    // Handle the case where only prefixes exist
    if (prefixes.length > 0 && suffixes.length === 0) {
      prefixes.map(prefix => {
        return updatedDatasets.map(dataset => {
          return generatedDatasetList.push({
            id: generateUniqueId(),
            title: `${prefix} ${dataset.title}`,
            categoryId: defaultCategory,
            dataType: dataset.dataType,
            accessType: dataset.accessType,
            unit: dataset.unit,
          });
        });
      });
    }

    // Handle the case where only suffixes exist
    if (suffixes.length > 0 && prefixes.length === 0) {
      updatedDatasets.map(dataset => {
        return suffixes.map(suffix => {
          return generatedDatasetList.push({
            id: generateUniqueId(),
            title: `${dataset.title} ${suffix}`,
            categoryId: defaultCategory,
            dataType: dataset.dataType,
            accessType: dataset.accessType,
            unit: dataset.unit,
          });
        });
      });
    }

    // Handle the case where both prefixes and suffixes exist
    if (prefixes.length > 0 && suffixes.length > 0) {
      prefixes.map(prefix => {
        return suffixes.map(suffix => {
          return updatedDatasets.map(dataset => {
            return generatedDatasetList.push({
              id: generateUniqueId(),
              title: `${prefix} ${dataset.title} ${suffix}`,
              categoryId: defaultCategory,
              dataType: dataset.dataType,
              accessType: dataset.accessType,
              unit: dataset.unit,
            });
          });
        });
      });
    }

    setDatasetsToCreate(generatedDatasetList);
    setAdvancedDialogVisibility(false);
    setAdvancedUsed(true);
  };

  const titleBodyTemplate = (options: ICommonDatasetProps) => {
    const dataset = datasetsToCreate.find(ds => ds.id === options.id);
    const currentTitle = dataset ? dataset.title : "";
    return (
      <InputText
        type="text"
        className="w-full"
        value={currentTitle}
        onChange={e => inputChangeHandler(options, e, "title")}
      />
    );
  };

  const dataTypeBodyTemplate = (options: ICommonDatasetProps) => {
    const dataset = datasetsToCreate.find(ds => ds.id === options.id);
    const currentDataType = dataset ? dataset.dataType : "";
    return (
      <Dropdown
        className="w-full"
        options={
          dataset?.accessType === DatasetAccessType.CONTINUOUS
            ? DATASET_DATA_TYPES_FOR_CONTINUOUS
            : DATASET_DATA_TYPES
        }
        value={currentDataType}
        onChange={e => inputChangeHandler(options, e, "dataType")}
      />
    );
  };

  const accessTypeBodyTemplate = (options: ICommonDatasetProps) => {
    const foundDataset = datasetsToCreate.find(dataset => dataset.id === options.id);
    if (foundDataset) {
      const currentAccessType = foundDataset.accessType;
      return (
        <Dropdown
          className="w-full"
          options={DATASET_ACCESS_TYPES}
          value={currentAccessType}
          onChange={e => inputChangeHandler(options, e, "accessType")}
        />
      );
    }
    return null;
  };
  const unitBodyTemplate = (options: ICommonDatasetProps) => {
    const dataset = datasetsToCreate.find(ds => ds.id === options.id);
    const unitSelectorValue = dataset ? dataset.unit : "";
    return (
      <DatasetUnitSelector
        className="w-full"
        value={unitSelectorValue}
        onChange={e => inputChangeHandler(options, e, "unit")}
      />
    );
  };

  const actionBodyTemplate = (options: ICommonDatasetProps) => {
    return (
      <Button
        icon="pi pi-trash"
        className="p-button p-button-danger"
        onClick={() => removeDatasetById(options.id)}
      />
    );
  };

  const inputChangeHandler = (
    options: ICommonDatasetProps,
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | DropdownChangeEvent,
    field: string
  ) => {
    const value = "value" in e ? e.value : e.target.value;

    setDatasetsToCreate(prevState => {
      const updatedDatasets = [...prevState];
      const dataset = updatedDatasets.find(ds => ds.id === options.id);

      if (dataset) {
        if (field === "accessType") {
          const newAccessType = value as DatasetAccessType;

          dataset.accessType = newAccessType;
          dataset.dataType = normalizeDataTypeForAccessType(newAccessType, dataset.dataType);
        } else {
          dataset[field] = value;

          if (field === "dataType") {
            dataset.dataType = value;
            dataset.dataType = normalizeDataTypeForAccessType(
              dataset.accessType as DatasetAccessType,
              dataset.dataType
            );
          }
        }
      }

      return updatedDatasets;
    });

    setLastDataset(options);
  };

  const removeDatasetById = (id: string) => {
    const updatedDatasets = [...datasetsToCreate];
    const index = updatedDatasets.findIndex(dataset => dataset.id === id);
    updatedDatasets.splice(index, 1);
    setDatasetsToCreate(updatedDatasets);
  };

  const { mutateAsync } = useCreateCategorizedDataset();

  async function createDatasetsHandler() {
    try {
      const emptyTitleIndex = datasetsToCreate.findIndex(dataset => dataset.title.trim() === "");

      if (emptyTitleIndex !== -1) {
        easasAlert({
          summary: T("error.validation-error"),
          detail: T("alert.title-cannot-be-empty"),
          severity: "error",
        });
        return;
      }

      if (datasetsToCreate.length === 0) {
        easasAlert({
          summary: T("error.validation-error"),
          detail: T("alert.no-datasets-to-create"),
          severity: "error",
        });
        return;
      }

      const results = await Promise.allSettled(
        datasetsToCreate.map(async dataset => {
          const data: Partial<BaseDatasetDTO> = {
            title: dataset.title,
            categoryId: defaultCategory,
            dataType: dataset.dataType,
            accessType: dataset.accessType,
            unit: dataset.unit,
          };

          return mutateAsync(data);
        })
      );

      const successfulMutations = results.filter(result => result.status === "fulfilled");
      const failedMutations = results.filter(result => result.status === "rejected");

      if (successfulMutations.length === results.length) {
        const currentCreatedDatasets = successfulMutations.map(result => {
          if (result.status === "fulfilled") {
            return result.value;
          }
          return null;
        });
        setCreatedDatasets(currentCreatedDatasets as Partial<BaseDatasetDTO>[]);
        easasAlert({
          summary: T("alert.datasets-created"),
          detail: T("alert.all-datasets-have-been-created"),
          severity: "success",
        });
      } else {
        easasAlert({
          summary: T("alert.creation-failed"),
          detail: T("error.datasets-failed-to-create", { count: failedMutations.length }),
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error creating datasets:", error);
      easasAlert({
        summary: T("alert.error"),
        detail: T("error.an-error-occurred-while-creating-datasets"),
        severity: "error",
      });
    }
  }

  const copyTextOnClick = (text: string, copyableText: string) => {
    return <CopyTextWithButton text={text} copyableText={copyableText} />;
  };

  return createdDatasets.length > 0 ? (
    <div className="flex col-12 align-items-center">
      <DataTable value={createdDatasets} className="w-full">
        <Column field="title" header={T("word.title")} body={e => copyTextOnClick(e.title, e.title)} />
        <Column
          field="identifier"
          header={T("word.identifier")}
          body={e => copyTextOnClick(e.identifier, e.identifier)}
        />
      </DataTable>
    </div>
  ) : (
    <div className="flex flex-column gap-3">
      {header}
      <div style={{ overflowY: "scroll", height: "100%", maxHeight: "50vh" }}>
        <DataTable value={datasetsToCreate}>
          <Column
            field="title"
            header={T("word.title")}
            style={{ width: "65%", padding: 0 }}
            body={titleBodyTemplate}
          />
          <Column
            field="dataType"
            header={T("word.type")}
            style={{ width: "10%", padding: 0 }}
            body={dataTypeBodyTemplate}
          />
          <Column
            field="accessType"
            header={T("label.access-type")}
            style={{ width: "10%", padding: 0 }}
            body={accessTypeBodyTemplate}
          />
          <Column
            field="unit"
            header={T("word.unit")}
            style={{ width: "10%", padding: 0 }}
            body={unitBodyTemplate}
          />
          <Column
            header={T("word.actions")}
            style={{ width: "5%", padding: 0, textAlign: "center" }}
            body={actionBodyTemplate}
          />
        </DataTable>
      </div>
      <div className="flex col-12 gap-2 justify-content-end">
        <Button
          className="component-editor-button icon-red"
          icon="pi pi-times"
          label={T("word.cancel")}
          onClick={closeDialog}
        />
        <Button
          className="component-editor-button icon-green"
          icon="pi pi-check"
          label={T("action.create")}
          onClick={() => createDatasetsHandler()}
        />
      </div>
    </div>
  );
};
export default CreateMultipleDatasets;
